import { Controller } from "@hotwired/stimulus"

/**
 * Relationship Builder Controller
 *
 * Provides a dropdown-based interface for building join expressions
 * between data sources, instead of requiring manual SQL entry.
 */
export default class extends Controller {
  static targets = [
    "sourceColumn",
    "targetColumn",
    "joinExpression",
    "generatedPreview",
    "testResult"
  ]

  static values = {
    sourceTable: String,
    targetTable: String,
    sourceColumns: Array,
    targetColumns: Array,
    testUrl: String
  }

  connect() {
    this.updatePreview()
  }

  // Called when source or target column dropdowns change
  updateJoin() {
    const sourceCol = this.hasSourceColumnTarget ? this.sourceColumnTarget.value : ''
    const targetCol = this.hasTargetColumnTarget ? this.targetColumnTarget.value : ''

    if (sourceCol && targetCol) {
      const expression = `${this.sourceTableValue}.${sourceCol} = ${this.targetTableValue}.${targetCol}`
      if (this.hasJoinExpressionTarget) {
        this.joinExpressionTarget.value = expression
      }
    }

    this.updatePreview()
  }

  // Update the preview display
  updatePreview() {
    if (!this.hasGeneratedPreviewTarget) return

    const expression = this.hasJoinExpressionTarget ? this.joinExpressionTarget.value : ''

    if (expression) {
      this.generatedPreviewTarget.innerHTML = `
        <code class="text-sm font-mono bg-base-200 px-2 py-1 rounded block">
          JOIN ${this.targetTableValue} ON ${expression}
        </code>
      `
      this.generatedPreviewTarget.classList.remove('hidden')
    } else {
      this.generatedPreviewTarget.classList.add('hidden')
    }
  }

  // Handle manual edits to the join expression textarea
  manualEdit() {
    this.updatePreview()
  }

  // Swap source and target columns (for bi-directional relationships)
  swapColumns() {
    const sourceVal = this.hasSourceColumnTarget ? this.sourceColumnTarget.value : ''
    const targetVal = this.hasTargetColumnTarget ? this.targetColumnTarget.value : ''

    if (this.hasSourceColumnTarget && this.hasTargetColumnTarget) {
      this.sourceColumnTarget.value = targetVal
      this.targetColumnTarget.value = sourceVal
      this.updateJoin()
    }
  }

  // Test the join expression
  async testJoin() {
    if (!this.hasTestUrlValue || !this.hasJoinExpressionTarget) return

    const expression = this.joinExpressionTarget.value
    if (!expression) {
      this.showTestResult(false, 'Please enter a join expression')
      return
    }

    this.showTestResult(null, 'Testing...')

    try {
      const response = await fetch(this.testUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ join_expression: expression })
      })

      const result = await response.json()

      if (result.valid) {
        this.showTestResult(true, `Valid! ${result.sample_count} sample rows`)
      } else {
        this.showTestResult(false, result.error || 'Invalid join expression')
      }
    } catch (error) {
      this.showTestResult(false, error.message)
    }
  }

  showTestResult(success, message) {
    if (!this.hasTestResultTarget) return

    this.testResultTarget.classList.remove('hidden')

    if (success === null) {
      // Loading state
      this.testResultTarget.innerHTML = `
        <span class="loading loading-spinner loading-xs"></span>
        <span class="text-sm">${message}</span>
      `
    } else if (success) {
      this.testResultTarget.innerHTML = `
        <svg class="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span class="text-sm text-success">${message}</span>
      `
    } else {
      this.testResultTarget.innerHTML = `
        <svg class="w-4 h-4 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        <span class="text-sm text-error">${message}</span>
      `
    }
  }
}
